import {Action, handleActions} from "redux-actions";
import {AppointmentState} from "./AppointmentState";
import {
	APPOINTMENT_CREATED,
	APPOINTMENT_DELETED,
	APPOINTMENT_TYPES_FETCHED,
	APPOINTMENT_UPDATED,
	JOB_APPOINTMENT_CREATED,
	JOB_APPOINTMENT_DELETED,
	JOB_APPOINTMENT_TYPES_FETCHED,
	JOB_APPOINTMENT_UPDATED,
	JOB_APPOINTMENTS_FETCHED,
	JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED,
	LEAD_APPOINTMENTS_FETCHED,
	LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED
} from "../../action/appointment/actionType";
import {none, option} from "ts-option";
import Appointment from "../../../model/appointment/Appointment";
import {objectIterator} from "../../../utils/DictionaryUtils";
import JobAppointment from "../../../model/appointment/job/JobAppointment";
import {toKeyIndexedObject} from "../../../utils/ArrayUtils";
import _ = require("underscore");

const initState: AppointmentState = {
	appointments: {}, appointmentTypes: none, jobAppointments: none, jobAppointmentTypes: none
};

const reducer = handleActions({
	[LEAD_APPOINTMENTS_FETCHED]: (state: AppointmentState, action: Action<any>): AppointmentState => {
		const appointments = {
			...state.appointments,
			[action.payload.leadId]: action.payload.appointments.sort(Appointment.compare)
		}
		return ({...state, appointments})
	},
	[APPOINTMENT_CREATED]: (state: AppointmentState, action: Action<APPOINTMENT_CREATED>): AppointmentState => {
		const leadId: string = action.payload.appointment.leadId.orNull;
		const appointments = {
			...state.appointments,
			[leadId]: (state.appointments[leadId] || []).concat(action.payload.appointment).sort(Appointment.compare)
		}
		return {...state, appointments}
	},
	[APPOINTMENT_UPDATED]: (state: AppointmentState, action: Action<APPOINTMENT_UPDATED>): AppointmentState => {
		const leadId: string = action.payload.appointment.leadId.orNull;
		const appointments = {
			...state.appointments,
			[leadId]: (state.appointments[leadId] || []).map(
				a => a.id == action.payload.appointment.id ? action.payload.appointment : a).sort(Appointment.compare)
		}
		return {...state, appointments}
	},
	[APPOINTMENT_DELETED]: (state: AppointmentState, action: Action<APPOINTMENT_DELETED>): AppointmentState => {
		const appointments = {
			...state.appointments,
			[action.payload.leadId]: (state.appointments[action.payload.leadId] || []).filter(
				app => app.id != action.payload.appointmentId)
		}
		return {...state, appointments}
	},
	[APPOINTMENT_TYPES_FETCHED]: (state: AppointmentState, action: Action<APPOINTMENT_TYPES_FETCHED>): AppointmentState => {
		const appointmentTypes = action.payload.appointmentTypes;
		const appointments = _.groupBy(_.flatten(objectIterator(state.appointments)).map(a => {
			const appointmentType = a.typeId.flatMap((atId: string) => option(appointmentTypes.filter(at => at.id == atId)[0]));
			return a.copy({type: appointmentType})
		}), app => app.leadId.orNull)
		return {...state, appointmentTypes: option(appointmentTypes), appointments}
	},
	[JOB_APPOINTMENTS_FETCHED]: (state: AppointmentState, action: Action<JOB_APPOINTMENTS_FETCHED>): AppointmentState => {
		const appointments = state.jobAppointments.getOrElse([]).filter(
			app => !(action.payload.jobAppointments.length > 0 && app.jobId.get ==
				action.payload.jobAppointments[0].jobId.get)).concat(
			action.payload.jobAppointments)
		return ({
			...state,
			jobAppointments: option(appointments.sort((a, b) => a.dateTime.diff(b.dateTime)))
		})
	},
	[JOB_APPOINTMENT_CREATED]: (state: AppointmentState, action: Action<JOB_APPOINTMENT_CREATED>): AppointmentState => {
		let appointments = state.jobAppointments.getOrElse([]);
		appointments = appointments.concat(action.payload.jobAppointment).sort((a, b) => a.dateTime.diff(b.dateTime));

		return {...state, jobAppointments: option(appointments)}
	},
	[JOB_APPOINTMENT_UPDATED]: (state: AppointmentState, action: Action<JOB_APPOINTMENT_UPDATED>): AppointmentState => {
		let appointments = state.jobAppointments.getOrElse([]);
		appointments = appointments.map(a => a.id == action.payload.jobAppointment.id ? action.payload.jobAppointment : a).sort(
			(a, b) => a.dateTime.diff(b.dateTime));

		return {...state, jobAppointments: option(appointments)}
	},
	[JOB_APPOINTMENT_DELETED]: (state: AppointmentState, action: Action<JOB_APPOINTMENT_DELETED>): AppointmentState => {
		const appointments = state.jobAppointments.getOrElse([]).filter(a => a.id != action.payload.appointmentId);
		return {...state, jobAppointments: option(appointments)}
	},
	[JOB_APPOINTMENT_TYPES_FETCHED]: (state: AppointmentState,
		action: Action<JOB_APPOINTMENT_TYPES_FETCHED>): AppointmentState => {
		const appointmentTypes = action.payload.jobAppointmentTypes;
		const appointments = state.jobAppointments.getOrElse([]).map(a => {
			const appointmentType = a.typeId.map(atId => appointmentTypes.filter(at => at.id == atId)[0]);
			return a.copy({type: appointmentType})
		});
		return {...state, jobAppointmentTypes: option(appointmentTypes), jobAppointments: option(appointments)}
	},
	[LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED]: (state: AppointmentState,
		action: Action<LEAD_APPOINTMENTS_IN_DATE_RANGE_FETCHED>): AppointmentState => {
		const appointments = {...state.appointments}

		Object.keys(action.payload.appointments).forEach(
			(leadId: string) => {
				const appointmentsMap = toKeyIndexedObject(appointments[leadId] || [], a => a.id);
				const newAppointmentsMap = toKeyIndexedObject(action.payload.appointments[leadId], a => a.id)
				const result = {...appointmentsMap, ...newAppointmentsMap}
				appointments[leadId] = objectIterator(result).sort(Appointment.compare);
			})
		return ({...state, appointments})
	},
	[JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED]: (state: AppointmentState,
		action: Action<JOB_APPOINTMENTS_IN_DATE_RANGE_FETCHED>): AppointmentState => {
		const appointmentsMap = toKeyIndexedObject(state.jobAppointments.getOrElse([]), a => a.id);
		const newAppointmentsMap = toKeyIndexedObject(action.payload.appointments, a => a.id)
		const result = {...appointmentsMap, ...newAppointmentsMap}
		const jobAppointments = objectIterator(result).sort(JobAppointment.compare)
		return ({...state, jobAppointments: option(jobAppointments)})
	},
}, initState);

export default reducer;

